import { range } from 'lodash-es';
import {
  ControllerFieldState,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import { posChangedField } from 'src/utils/posFieldUtils';
import { PurchaseTicketsInput } from 'src/utils/purchaseUtils';
import { fitsSeatIncrementType, getNextTicketBy } from 'src/utils/ticketUtils';
import { Ticket } from 'src/WebApiController';

const DEFAULT_INCREMENTS = [1, 2];

export const getIncrement = (
  from: number | string,
  to: number | string | null,
  quantity: number
): number | null => {
  if (quantity <= 1) return null;
  if (to === null) return 1;

  if (typeof from === 'number' && typeof to === 'number') {
    if (to < from) {
      return null;
    }
    return Math.round((to - from) / (quantity - 1));
  }

  if (
    typeof from === 'string' &&
    typeof to === 'string' &&
    from.length === 1 &&
    to.length === 1
  ) {
    if (to.charCodeAt(0) < from.charCodeAt(0)) {
      return null;
    }

    return Math.round((to.charCodeAt(0) - from.charCodeAt(0)) / (quantity - 1));
  }

  // Unsupported cases - we just try if any of DEFAULT_INCREMENTS fits
  // and if not, we return null
  const fromStr = from.toString();
  const toStr = to.toString();

  for (const increment of DEFAULT_INCREMENTS) {
    if (fitsSeatIncrementType(fromStr, toStr, quantity, increment)) {
      return increment;
    }
  }

  return null;
};

export const generateSeats = (
  from: number | string,
  to: number | string | null,
  quantity: number,
  populateNextSeats?: boolean
): (string | number)[] => {
  if (!to && !populateNextSeats) {
    // If only `from` is defined
    return Array.from({ length: quantity }, (_, i) => (i === 0 ? from : ''));
  }

  const increment = getIncrement(from, to, quantity);
  if (increment === null) {
    return [from];
  }

  const seats: (string | number)[] = [from];
  for (let i = 1; i < quantity; i++) {
    seats.push(getNextTicketBy(seats[i - 1], increment));
  }

  return seats;
};

export function assignTicketGroupSeats(
  seatFrom: string | number | null,
  seatTo: string | number | null,
  quantity: number | null,
  tickets: Ticket[] | null,
  setValue: UseFormSetValue<PurchaseTicketsInput>,
  setError: UseFormSetError<PurchaseTicketsInput>,
  clearErrors: UseFormClearErrors<PurchaseTicketsInput>,
  fieldState: ControllerFieldState,
  rowIndex: number,
  skipError?: string,
  populateNextSeats?: boolean
) {
  if (!quantity) return;

  let seats: (string | number)[] = [];

  // No seats case: No seatFrom and no seatTo
  if (seatFrom == null && seatTo == null) {
    seats = Array.from({ length: quantity }, () => '');
  } else if (seatFrom != null && seatTo != null) {
    //  Numeric Case: Both seatFrom and seatTo are numbers (or numeric strings)
    if (!isNaN(Number(seatFrom)) && !isNaN(Number(seatTo))) {
      const seatFromNum = Number(seatFrom);
      const seatToNum = Number(seatTo);

      if (
        !validateSeatRange(
          seatFromNum,
          seatToNum,
          tickets,
          quantity,
          rowIndex,
          setError,
          clearErrors,
          fieldState,
          skipError
        )
      ) {
        // If validation fails, adjust existing tickets if available.
        if (tickets) {
          if (quantity < tickets.length) {
            clearErrors(`ticketGroups.${rowIndex}.tickets`);
            setValue(
              `ticketGroups.${rowIndex}.tickets`,
              posChangedField(tickets.slice(0, quantity))
            );
            return;
          }
          if (quantity > tickets.length && tickets[0]) {
            const startOrdinal = tickets[0].ord || 0;
            const initialTickets = tickets[0];

            const increment =
              getIncrement(
                tickets[0].seat,
                tickets[tickets.length - 1].seat,
                tickets.length
              ) ?? 1;

            const newTickets = range(tickets.length, quantity).map((i) => ({
              ...initialTickets,
              id: startOrdinal + i,
              ord: startOrdinal + i,
              lstOrd: startOrdinal + i,
              seat: populateNextSeats
                ? getNextTicketBy(tickets![0].seat, increment * i).toString()
                : '',
            }));
            if (populateNextSeats) {
              clearErrors(`ticketGroups.${rowIndex}.tickets`);
            }
            setValue(
              `ticketGroups.${rowIndex}.tickets`,
              posChangedField([...tickets, ...newTickets])
            );
            return;
          }
        }
      }
      seats = generateSeats(seatFromNum, seatToNum, quantity);
    }
    // Character Case: Both seatFrom and seatTo are single letters
    else if (
      typeof seatFrom === 'string' &&
      typeof seatTo === 'string' &&
      seatFrom.length === 1 &&
      seatTo.length === 1
    ) {
      const fromCharCode = seatFrom.toUpperCase().charCodeAt(0);
      const toCharCode = seatTo.toUpperCase().charCodeAt(0);

      if (
        !validateSeatRange(
          fromCharCode,
          toCharCode,
          tickets,
          quantity,
          rowIndex,
          setError,
          clearErrors,
          fieldState,
          skipError
        )
      ) {
        return;
      }
      seats = generateSeats(seatFrom, seatTo, quantity);
    }
  }
  //  Only seatFrom provided (Numeric or Char)
  else if (seatFrom != null) {
    seats = generateSeats(seatFrom, null, quantity, populateNextSeats);
  }
  // Only seatTo provided (Numeric or Char)
  else if (seatTo != null) {
    seats = generateSeats(seatTo, null, quantity);
  }

  // Assign seats to tickets. If tickets do not exist, create new ones.
  if (!tickets) {
    tickets = seats.map((seat, i) => ({
      id: i,
      ord: i,
      lstOrd: i,
      seat: seat.toString(),
    })) as Ticket[];
  }

  if (tickets) {
    if (tickets.length > quantity) {
      // If there are too many tickets, slice the array down.
      tickets = tickets.slice(0, quantity);
    } else if (tickets.length < quantity) {
      // If there are fewer tickets than the new quantity,
      // use the first ticket to create additional tickets.
      const origTicket = tickets[0];
      const startOrdinal = origTicket?.ord ?? 0;
      const additionalTickets = range(tickets.length, quantity).map((i) => ({
        ...origTicket, // clone the template (if available) or create a new ticket if needed
        id: startOrdinal + i,
        ord: startOrdinal + i,
        lstOrd: startOrdinal + i,
        seat: '', // initialize seat as blank
      }));
      tickets = [...tickets, ...additionalTickets];
    }

    seats.forEach((seat, i) => {
      if (tickets![i]) {
        tickets![i].seat = seat.toString();
      }
    });
  }

  setValue(`ticketGroups.${rowIndex}.tickets`, posChangedField(tickets));
}

// Validate the seat range and quantity. Set errors if necessary.
export function validateSeatRange(
  seatFrom: number,
  seatTo: number,
  tickets: Ticket[] | null,
  quantity: number,
  rowIndex: number,
  setError: UseFormSetError<PurchaseTicketsInput>,
  clearErrors: UseFormClearErrors<PurchaseTicketsInput>,
  fieldState: ControllerFieldState,
  skipError?: string
): boolean {
  if (quantity !== tickets?.length) {
    setError(`ticketGroups.${rowIndex}.tickets`, {
      message:
        'Quantity does not match existing tickets. Please re-enter seat range or change quantity',
    });
    return false;
  }

  if (quantity === 1 && seatFrom !== seatTo) {
    const hasDuplicates = false;
    if (tickets) {
      const seatSet = new Set();
      for (const t of tickets) {
        if (!t.seat) {
          setError(`ticketGroups.${rowIndex}.tickets`, {
            message: 'Seats cannot be empty',
          });
          return false;
        }
        if (seatSet.has(t.seat)) {
          setError(`ticketGroups.${rowIndex}.tickets`, {
            message: 'Duplicate seats are not allowed',
          });
          return false;
        }
        seatSet.add(t.seat);
      }
    }

    setError(`ticketGroups.${rowIndex}.tickets`, {
      message: 'Unable to calculate seating. Please manually allocate seats',
    });
    return false;
  }
  if (seatFrom > seatTo) {
    setError(`ticketGroups.${rowIndex}.tickets`, {
      message: 'End seat must be greater than start seat',
    });
    return false;
  }

  const totalRange = seatTo - seatFrom + 1;
  if (quantity > totalRange) {
    setError(`ticketGroups.${rowIndex}.tickets`, {
      message: 'Quantity not within range',
    });
    return false;
  }

  if (
    fieldState.error?.message &&
    // Prevent clearing the error if the error is set at the TicketGroup level
    (!skipError || !fieldState.error?.message.includes(skipError))
  ) {
    clearErrors(`ticketGroups.${rowIndex}.tickets`);
  }

  return true;
}
